import React, { useEffect } from "react";
import classNames from "classnames";
import { SelectedParagraphHeading } from "./SelectedParagraphHeading";
import {
  SelectedParagraphStyleTile,
  SelectedParagraphStyleTileProps,
} from "./SelectedParagraphStyleTile";
import "./SelectedParagraph.css";

import type { SelectedParagraphHeadingProps } from "./SelectedParagraphHeading";
import {
  SelectedParagraphControls,
  SelectedParagraphControlsProps,
} from "./SelectedParagraphControls";

export interface SelectedParagraphProps
  extends
    SelectedParagraphHeadingProps,
    SelectedParagraphStyleTileProps,
    Pick<
      SelectedParagraphControlsProps,
      | "outlineMode"
      | "restyleAllStyle"
      | "totalTracked"
      | "tracked"
      | "onApprove"
      | "onApproveAll"
      | "onRestyle"
      | "onRestyleAll"
    > {}

export function SelectedParagraph(props: SelectedParagraphProps) {
  const { loading, previousDisabled, nextDisabled, onPrevious, onNext } = props;

  // Global listener so arrow keys work regardless of focus.
  // (alternate plan would be using onKeyDown on a container, which requires tabIndex + user click, to transfer focus)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      if (e.key === "ArrowLeft" && !loading && !previousDisabled) {
        e.preventDefault();
        onPrevious?.();
      } else if (e.key === "ArrowRight" && !loading && !nextDisabled) {
        e.preventDefault();
        onNext?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [loading, previousDisabled, nextDisabled, onPrevious, onNext]);

  // Mirrors the Accept buttons' enabled condition in SelectedParagraphControls:
  // the card only goes blue when at least one of them can actually be clicked.
  const actionable =
    !props.loading &&
    !props.notInProfile &&
    !props.noSelectedCharacterStyle &&
    ((props.tracked ?? 0) > 0 || (props.totalTracked ?? 0) > 0);

  return (
    <div
      className={classNames("selected-item", {
        "selected-item-actionable": actionable,
        "selected-item-no-selected-character-style": props.noSelectedCharacterStyle,
        "selected-item-not-in-profile": props.notInProfile,
      })}
    >
      <SelectedParagraphHeading
        isMultiSelect={props.isMultiSelect}
        loading={props.loading}
        nextDisabled={props.nextDisabled}
        noSelectedCharacterStyle={props.noSelectedCharacterStyle}
        outlineMode={props.outlineMode}
        previousDisabled={props.previousDisabled}
        selectionBounds={props.selectionBounds}
        total={props.total}
        onNext={props.onNext}
        onPrevious={props.onPrevious}
      />
      <SelectedParagraphControls
        isMultiSelect={props.isMultiSelect}
        loading={props.loading}
        noSelectedCharacterStyle={props.noSelectedCharacterStyle}
        notInProfile={props.notInProfile}
        outlineMode={props.outlineMode}
        restyleAllStyle={props.restyleAllStyle}
        selectedStyle={props.selectedStyle}
        totalTracked={props.totalTracked}
        tracked={props.tracked}
        onApprove={props.onApprove}
        onApproveAll={props.onApproveAll}
        onRestyle={props.onRestyle}
        onRestyleAll={props.onRestyleAll}
      />
      <SelectedParagraphStyleTile
        isMultiSelect={props.isMultiSelect}
        loading={props.loading}
        noSelectedCharacterStyle={props.noSelectedCharacterStyle}
        notInProfile={props.notInProfile}
        selectedItem={props.selectedItem}
        selectedStyle={props.selectedItem?.currentStyle}
      />
    </div>
  );
}
